import { useState } from "react";
import { Calendar, Shield, Users, Zap } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { supabase } from "../../../lib/supabase";

interface FeatureProps {
  icon: React.ReactNode;
  title: string;
  description: string;
}

function Feature({ icon, title, description }: FeatureProps) {
  return (
    <div className="flex items-start gap-3">
      <div className="bg-amber-50 p-2 rounded-lg">{icon}</div>
      <div>
        <h3 className="font-medium text-gray-900">{title}</h3>
        <p className="text-sm text-gray-500">{description}</p>
      </div>
    </div>
  );
}

export function LoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [isResetMode, setIsResetMode] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      navigate("/");
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) throw error;

      setResetSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const toggleResetMode = () => {
    setIsResetMode(!isResetMode);
    setError(null);
    setResetSent(false);
  };

  const handleGoogleSignIn = () => {
    // Implementation of Google sign-in
  };

  return (
    <div className="min-h-screen flex bg-gray-50">
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="max-w-md w-full">
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-br from-vincer-500 to-vincer-400 mb-3">
              <Zap className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Welcome to VINCER
            </h1>
            <p className="text-gray-600">
              {isResetMode
                ? "Reset your password to access your account"
                : "Sign in to manage your mentorship sessions"}
            </p>
          </div>

          {resetSent ? (
            <div className="bg-white p-6 rounded-xl shadow-sm border">
              <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm">
                Password reset instructions have been sent to your email
                address.
              </div>
              <button
                onClick={toggleResetMode}
                className="mt-4 w-full flex justify-center py-2 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-black bg-vincer-500 hover:bg-vincer-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-vincer-500 transition-colors"
              >
                Back to Login
              </button>
            </div>
          ) : (
            <form
              onSubmit={isResetMode ? handleResetPassword : handleSubmit}
              className="bg-white p-6 rounded-xl shadow-sm border space-y-4"
            >
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                  {error}
                </div>
              )}

              <div className="space-y-2">
                <label
                  htmlFor="email-address"
                  className="block text-sm font-medium text-gray-700"
                >
                  Email
                </label>
                <input
                  id="email-address"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-vincer-500 focus:border-vincer-500 sm:text-sm"
                  placeholder="Email address"
                />
              </div>

              {!isResetMode && (
                <div className="space-y-2">
                  <label
                    htmlFor="password"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Password
                  </label>
                  <input
                    id="password"
                    name="password"
                    type="password"
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-vincer-500 focus:border-vincer-500 sm:text-sm"
                    placeholder="Password"
                  />
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-vincer-500 hover:bg-vincer-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-vincer-500 transition-colors"
              >
                {loading
                  ? isResetMode
                    ? "Sending reset instructions..."
                    : "Signing in..."
                  : isResetMode
                    ? "Reset Password"
                    : "Sign in"}
              </button>

              {!isResetMode && (
                <div className="text-center mt-2">
                  <button
                    type="button"
                    onClick={toggleResetMode}
                    className="text-sm text-gray-600 hover:text-gray-900"
                  >
                    Forgot your password?
                  </button>
                </div>
              )}

              {isResetMode && (
                <div className="text-center mt-2">
                  <button
                    type="button"
                    onClick={toggleResetMode}
                    className="text-sm text-gray-600 hover:text-gray-900"
                  >
                    Back to login
                  </button>
                </div>
              )}
            </form>
          )}
        </div>
      </div>

      <div className="hidden lg:flex lg:flex-1 bg-gradient-to-br from-amber-50 to-white p-8 items-center justify-center">
        <div className="max-w-md space-y-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">
            Streamline Your Mentorship Program
          </h2>

          <div className="space-y-4">
            <Feature
              icon={<Calendar className="w-5 h-5 text-vincer-500" />}
              title="Smart Scheduling"
              description="Automatically find the perfect time for mentorship sessions using our intelligent calendar integration."
            />

            <Feature
              icon={<Users className="w-5 h-5 text-vincer-500" />}
              title="Easy Matching"
              description="Connect mentors and mentees effortlessly with our intuitive matching system."
            />

            <Feature
              icon={<Shield className="w-5 h-5 text-vincer-500" />}
              title="Secure Platform"
              description="Your data is protected with enterprise-grade security and encryption."
            />
          </div>
        </div>
      </div>
    </div>
  );
}
